// mockData.ts

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PassOnItem {
  id: string
  timeLabel: string
  officer: string
  message: string
}

export interface BriefingSubSection {
  title: string
  body: string
}

export interface BriefingRepeatItem {
  name: string
  context: string
}

export interface StatRow {
  label: string
  total: string
  wk1: number
  wk2: number
  delta: number
}

export interface WeekData {
  passOnItems: PassOnItem[]
  shiftContinuityCopy: {
    sectionLabel: string
    title: string
    footer: string
  }
  commandSummaryCopy: {
    sectionLabel: string
    headline: string
    confidenceLabel: string
    confidenceTooltip: string
    contextLine: string
    cardLabel: string
    readMoreLabel: string
    bullets: string[]
  }
  briefingCopy: {
    headerLabel: string
    dateRange: string
    printLabel: string
    whyItMatters: { title: string; body: string }
    sections: {
      weeklyOverview: {
        title: string
        body: string
        bullets: string[]
        statRows: StatRow[]
      }
      notablePatterns: { title: string; subSections: BriefingSubSection[] }
      priorityEntities: { title: string; items: BriefingRepeatItem[] }
      operationalGaps: { title: string; bullets: string[] }
      recommendedActions: { title: string; items: string[] }
      sourceNotes: { title: string; body: string }
    }
  }
}

// ─── 7-Day Data ───────────────────────────────────────────────────────────────

export const data7Day: WeekData = {
  passOnItems: [
    {
      id: 'p1',
      timeLabel: 'Mon 22:00',
      officer: 'Ofc. Ryan Bourque',
      message:
        'Marcus Johnson at 412 Evangeline St. Third call this week. Escalation noted. Document thoroughly.',
    },
    {
      id: 'p2',
      timeLabel: 'Tue 06:30',
      officer: 'Ofc. Sarah Brasseaux',
      message:
        'Juvenile group at Bayou Oaks Park again around 0200. Same group. Parents not reachable.',
    },
    {
      id: 'p3',
      timeLabel: 'Wed 14:00',
      officer: 'Ofc. Caleb Landry',
      message:
        'Michael Reyes stopped on LA-31 near Bridge St. Active warrant confirmed. Arrest made.',
    },
    {
      id: 'p4',
      timeLabel: 'Thu 08:00',
      officer: 'Ofc. Melissa Williams',
      message:
        'Welfare check at 88 Cypress St. Resident OK. Follow up requested next week.',
    },
    {
      id: 'p5',
      timeLabel: 'Thu 23:45',
      officer: 'Ofc. Daniel Smith',
      message:
        'Two vehicles at Sugar Mill Rd property. Subjects fled on approach. Area cleared.',
    },
    {
      id: 'p6',
      timeLabel: 'Fri 07:00',
      officer: 'Ofc. Alyssa Broussard',
      message:
        'Brian Okafor reported missing bicycle. Likely civil issue. Note documented.',
    },
  ],
  shiftContinuityCopy: {
    sectionLabel: 'Shift Handoff',
    title: 'Pass Ons',
    footer: 'Last updated Fri Feb 28, 07:00. End of week review.',
  },
  commandSummaryCopy: {
    sectionLabel: 'Weekly Command Summary',
    headline:
      '412 Evangeline St is escalating. Three contacts in five days with tone shift across shifts.',
    confidenceLabel: 'Source Confidence: High',
    confidenceTooltip:
      'Based on note volume, cross-shift participation, and call documentation.',
    contextLine: 'Compiled from 38 officer notes across all shifts.',
    cardLabel: 'Command Summary',
    readMoreLabel: 'Open Operational Brief',
    bullets: [
      '412 Evangeline St and Marcus Johnson account for most of the domestic increase this week. Escalation noted by multiple officers.',
      'Bayou Oaks Park juvenile group contacted seven times. Third consecutive week of elevated activity. Parents not consistently reachable.',
      'LA-31 late night vehicle activity continues between 0200 and 0400. No subjects identified across three nights.',
    ],
  },
  briefingCopy: {
    headerLabel: 'Operational Briefing',
    dateRange: 'Feb 21 to Feb 27, 2026',
    printLabel: 'Print',
    whyItMatters: {
      title: 'Why This Matters',
      body: 'This week shows two developing patterns. Domestic calls at 412 Evangeline St are increasing in frequency and intensity across shifts. Bayou Oaks Park juvenile contacts have continued for a third straight week without consistent parental engagement. Both patterns warrant command awareness before they require reactive enforcement.',
    },
    sections: {
      weeklyOverview: {
        title: 'Weekly Overview',
        body: '27 calls for service this week, up from 21 last week. Domestic calls drove the increase. Three arrests made. No major property crime reported.',
        bullets: [
          'Domestic calls: 6. Last week: 3.',
          'Juvenile contacts elevated at Bayou Oaks Park.',
          'LA-31 late night activity ongoing. No IDs made.',
          'Officer coverage consistent. No shift gaps.',
        ],
        statRows: [
          {
            label: 'Domestic calls',
            total: '6 total',
            wk1: 3,
            wk2: 6,
            delta: 3,
          },
          {
            label: 'Juvenile contacts',
            total: '7 total',
            wk1: 4,
            wk2: 7,
            delta: 3,
          },
          {
            label: 'LA-31 activity',
            total: '3 total',
            wk1: 0,
            wk2: 3,
            delta: 3,
          },
          { label: 'Arrests made', total: '3 total', wk1: 1, wk2: 3, delta: 2 },
        ],
      },
      notablePatterns: {
        title: 'Notable Patterns',
        subSections: [
          {
            title: 'Domestic Incidents',
            body: '412 Evangeline St had three calls in five days. Marcus Johnson present each time. Two officers documented escalating tone. No injuries reported. Pattern should be reviewed for safety planning.',
          },
          {
            title: 'Property Crimes',
            body: 'Two overnight vehicle break-ins along the LA-31 corridor. No suspects identified. Possible relation to late-night vehicle activity. No confirmed linkage at this time.',
          },
          {
            title: 'Juvenile Contacts',
            body: 'Approximately four juveniles contacted seven times at Bayou Oaks Park. Multiple contacts after midnight. Devin Torres identified as primary contact. Parents reached twice. Unreachable on two occasions.',
          },
          {
            title: 'Community Issues',
            body: '88 Cypress St had two neighbor-initiated welfare checks. Resident found safe both times. Sugar Mill Rd had two trespassing complaints. Subjects fled prior to identification.',
          },
        ],
      },
      priorityEntities: {
        title: 'Priority Entities',
        items: [
          {
            name: 'Marcus Johnson',
            context:
              'Five contacts this week. Domestic and welfare related. Present at all three 412 Evangeline St calls.',
          },
          {
            name: '412 Evangeline St',
            context:
              'Six contacts this week. Highest call volume location. Majority domestic.',
          },
          {
            name: 'Devin Torres',
            context:
              'Four contacts at Bayou Oaks Park. Associated with late-night juvenile group.',
          },
          {
            name: 'LA-31 (Bridge St)',
            context:
              'Four contacts. Repeated late-night vehicle activity. One warrant arrest occurred in this corridor.',
          },
        ],
      },
      operationalGaps: {
        title: 'Operational Gaps',
        bullets: [
          'LA-31 late-night activity identified. No subjects identified after three nights.',
          'Bayou Oaks Park juvenile contacts continue. No formal intervention initiated.',
          '412 Evangeline St escalation documented. No safety plan referral made.',
          'Sugar Mill Rd trespassing complaints unresolved. No subjects identified.',
        ],
      },
      recommendedActions: {
        title: 'Recommended Actions',
        items: [
          'Assign monitoring officer for 412 Evangeline St and Marcus Johnson.',
          'Increase patrol presence on LA-31 between 0200 and 0400.',
          'Coordinate with school or community services regarding Bayou Oaks Park juveniles.',
          'Schedule follow-up welfare check at 88 Cypress St.',
          'Advise Sugar Mill Rd property owner on no-trespass process.',
          'Brief incoming supervisors on 412 Evangeline St domestic pattern.',
        ],
      },
      sourceNotes: {
        title: 'Source Notes',
        body: '38 notes reviewed including call logs and informal field notes. Last updated Feb 27, 2026 at 06:14. Source confidence high.',
      },
    },
  },
}

// ─── 14-Day Data ──────────────────────────────────────────────────────────────

export const data14Day: WeekData = {
  passOnItems: [
    {
      id: 'q1',
      timeLabel: 'Feb 14 21:30',
      officer: 'Ofc. Caleb Landry',
      message:
        'First contact at 412 Evangeline St this period. Verbal dispute. No injuries. Documented.',
    },
    {
      id: 'q2',
      timeLabel: 'Feb 15 02:15',
      officer: 'Ofc. Jared Hebert',
      message:
        'Bayou Oaks Park. Four juveniles present at 0215. Dispersed without incident.',
    },
    {
      id: 'q3',
      timeLabel: 'Feb 17 19:00',
      officer: 'Ofc. Sarah Brasseaux',
      message:
        'Devin Torres contacted near St. Martinville High after hours. Parents notified.',
    },
    {
      id: 'q4',
      timeLabel: 'Feb 19 08:30',
      officer: 'Ofc. Melissa Williams',
      message:
        'Welfare check at 88 Cypress St. Resident answered. No concerns noted.',
    },
    {
      id: 'q5',
      timeLabel: 'Feb 20 23:00',
      officer: 'Ofc. Daniel Smith',
      message:
        'Suspicious vehicle along LA-31 near Bridge St. Plates run. No hits. Vehicle left area.',
    },
    {
      id: 'q6',
      timeLabel: 'Feb 21 06:45',
      officer: 'Ofc. Alyssa Broussard',
      message:
        'Trespassing complaint on Sugar Mill Rd. No subjects on arrival. Owner advised.',
    },
    {
      id: 'q7',
      timeLabel: 'Feb 22 18:40',
      officer: 'Ofc. Jared Hebert',
      message:
        'Welfare check for Marcus Johnson at 412 Evangeline St. Appeared intoxicated. No further action.',
    },
    {
      id: 'q8',
      timeLabel: 'Feb 24 21:15',
      officer: 'Ofc. Sarah Brasseaux',
      message:
        'Second domestic call at 412 Evangeline St. Tone escalating. Documented by multiple officers.',
    },
  ],
  shiftContinuityCopy: {
    sectionLabel: 'Shift Handoff',
    title: 'Pass Ons',
    footer: 'Last updated Feb 27, 2026. Two week review.',
  },
  commandSummaryCopy: {
    sectionLabel: 'Two Week Command Summary',
    headline:
      'Domestic pattern at 412 Evangeline St developing over two weeks. Frequency and tone both increasing.',
    confidenceLabel: 'Source Confidence: High',
    confidenceTooltip:
      'Based on note volume, cross-shift participation, and call documentation.',
    contextLine: 'Compiled from 74 officer notes across two weeks.',
    cardLabel: 'Command Summary',
    readMoreLabel: 'Open Operational Brief',
    bullets: [
      '412 Evangeline St has had five contacts over fourteen days. Tone shift noted in the second week by multiple officers.',
      'Bayou Oaks Park juvenile activity began in week one and continued into week two. Same group each time.',
      'LA-31 late-night vehicle activity first observed Feb 20. Pattern developing but not yet confirmed.',
    ],
  },
  briefingCopy: {
    headerLabel: 'Operational Briefing',
    dateRange: 'Feb 14 to Feb 27, 2026',
    printLabel: 'Print',
    whyItMatters: {
      title: 'Why This Matters',
      body: 'The two week view reveals a pattern that was not visible in the single week snapshot. The 412 Evangeline St situation began as an isolated contact and has developed into a recurring pattern with escalating tone. The juvenile contacts at Bayou Oaks Park have been consistent across both weeks. Early identification of these trends allows for proactive intervention.',
    },
    sections: {
      weeklyOverview: {
        title: 'Two Week Overview',
        body: '48 calls for service over fourteen days. Domestic calls account for the largest single category increase. Four arrests made. Juvenile contacts elevated in both weeks.',
        bullets: [
          'Domestic calls: 9 over two weeks.',
          'Juvenile contacts: 11 total. Concentrated at Bayou Oaks Park.',
          'LA-31 activity began in week two.',
          'Officer coverage consistent across both weeks.',
        ],
        statRows: [
          {
            label: 'Domestic calls',
            total: '9 total',
            wk1: 4,
            wk2: 5,
            delta: 1,
          },
          {
            label: 'Juvenile contacts',
            total: '11 total',
            wk1: 5,
            wk2: 6,
            delta: 1,
          },
          {
            label: 'LA-31 activity',
            total: '3 total',
            wk1: 0,
            wk2: 3,
            delta: 3,
          },
          { label: 'Arrests made', total: '4 total', wk1: 1, wk2: 3, delta: 2 },
        ],
      },
      notablePatterns: {
        title: 'Notable Patterns',
        subSections: [
          {
            title: 'Domestic Incidents',
            body: '412 Evangeline St had two contacts in week one and three in week two. Marcus Johnson present at all five. Tone escalation documented in week two by separate officers.',
          },
          {
            title: 'Property Crimes',
            body: 'Three vehicle-related incidents across the two week period. Two break-ins and one suspicious vehicle stop. No suspects identified in any case.',
          },
          {
            title: 'Juvenile Contacts',
            body: 'Bayou Oaks Park juvenile group contacted across both weeks. Devin Torres identified as consistent presence. Late-night contacts increasing in frequency.',
          },
          {
            title: 'Community Issues',
            body: 'Two welfare checks at 88 Cypress St. Two trespassing complaints on Sugar Mill Rd. Both locations showing repeat contact patterns.',
          },
        ],
      },
      priorityEntities: {
        title: 'Priority Entities',
        items: [
          {
            name: 'Marcus Johnson',
            context:
              'Eight contacts over two weeks. Present at all five 412 Evangeline St calls. Escalating behavior documented.',
          },
          {
            name: '412 Evangeline St',
            context:
              'Five contacts over two weeks. Pattern developing from week one to week two.',
          },
          {
            name: 'Devin Torres',
            context:
              'Six contacts at Bayou Oaks Park across both weeks. Consistent late-night presence.',
          },
          {
            name: 'Bayou Oaks Park',
            context:
              'Eleven juvenile contacts over fourteen days. Same group each time.',
          },
        ],
      },
      operationalGaps: {
        title: 'Operational Gaps',
        bullets: [
          '412 Evangeline St pattern visible for two weeks. No safety plan initiated.',
          'Bayou Oaks Park juvenile group active for two weeks. No formal outreach made.',
          'LA-31 activity emerging. No increased patrol assigned yet.',
          'Sugar Mill Rd trespassing unresolved across both weeks.',
        ],
      },
      recommendedActions: {
        title: 'Recommended Actions',
        items: [
          'Initiate safety planning review for 412 Evangeline St given two week pattern.',
          'Assign dedicated juvenile outreach contact for Bayou Oaks Park group.',
          'Begin increased LA-31 overnight patrol.',
          'Follow up welfare check at 88 Cypress St.',
          'Issue no-trespass notice for Sugar Mill Rd property owner.',
          'Brief all shift supervisors on two week domestic escalation trend.',
        ],
      },
      sourceNotes: {
        title: 'Source Notes',
        body: '74 notes reviewed across two weeks including call logs and field notes. Last updated Feb 27, 2026 at 06:14. Source confidence high.',
      },
    },
  },
}

// ─── 30-Day Data ──────────────────────────────────────────────────────────────

export const data30Day: WeekData = {
  passOnItems: [
    {
      id: 'r1',
      timeLabel: 'Feb 01 20:00',
      officer: 'Ofc. Caleb Landry',
      message:
        'Noise complaint at 412 Evangeline St. First contact this month. Resolved on scene.',
    },
    {
      id: 'r2',
      timeLabel: 'Feb 04 01:30',
      officer: 'Ofc. Jared Hebert',
      message:
        'Bayou Oaks Park. Juveniles present after midnight. First contact this month. Dispersed.',
    },
    {
      id: 'r3',
      timeLabel: 'Feb 08 22:00',
      officer: 'Ofc. Melissa Williams',
      message: 'Welfare check at 88 Cypress St. Neighbor concern. Resident OK.',
    },
    {
      id: 'r4',
      timeLabel: 'Feb 11 14:00',
      officer: 'Ofc. Daniel Smith',
      message:
        'Trespassing complaint on Sugar Mill Rd. Two subjects fled. Owner notified.',
    },
    {
      id: 'r5',
      timeLabel: 'Feb 14 21:30',
      officer: 'Ofc. Alyssa Broussard',
      message:
        '412 Evangeline St. Verbal dispute. Marcus Johnson present. Tone elevated. Documented.',
    },
    {
      id: 'r6',
      timeLabel: 'Feb 18 03:00',
      officer: 'Ofc. Sarah Brasseaux',
      message:
        'LA-31 (Bridge St corridor). Suspicious vehicle. Plates run. No hits. Vehicle departed.',
    },
    {
      id: 'r7',
      timeLabel: 'Feb 22 18:40',
      officer: 'Ofc. Jared Hebert',
      message:
        'Welfare check for Marcus Johnson at 412 Evangeline St. Appeared intoxicated. No further action.',
    },
    {
      id: 'r8',
      timeLabel: 'Feb 24 21:15',
      officer: 'Ofc. Caleb Landry',
      message:
        'Domestic call at 412 Evangeline St. Escalation noted. Third contact this month.',
    },
    {
      id: 'r9',
      timeLabel: 'Feb 26 02:00',
      officer: 'Ofc. Melissa Williams',
      message:
        'Bayou Oaks Park. Devin Torres and group. Fourth contact this month. Parents unreachable.',
    },
    {
      id: 'r10',
      timeLabel: 'Feb 27 07:00',
      officer: 'Ofc. Sarah Brasseaux',
      message:
        'Michael Reyes stopped on LA-31. Active warrant. Arrested and transported.',
    },
  ],
  shiftContinuityCopy: {
    sectionLabel: 'Shift Handoff',
    title: 'Pass Ons',
    footer: 'Last updated Feb 27, 2026. Thirty day review.',
  },
  commandSummaryCopy: {
    sectionLabel: 'Monthly Command Summary',
    headline:
      'Two sustained patterns identified over the last 30 days. Both require intervention.',
    confidenceLabel: 'Source Confidence: High',
    confidenceTooltip:
      'Based on note volume, cross-shift participation, and call documentation.',
    contextLine: 'Compiled from 142 officer notes across thirty days.',
    cardLabel: 'Command Summary',
    readMoreLabel: 'Open Operational Brief',
    bullets: [
      '412 Evangeline St has had eight contacts over thirty days. Escalation trajectory confirmed in final two weeks.',
      'Bayou Oaks Park juvenile group active throughout the month. Frequency accelerating in final two weeks.',
      'LA-31 late-night vehicle activity emerged mid-month. Pattern now established in the corridor.',
    ],
  },
  briefingCopy: {
    headerLabel: 'Operational Briefing',
    dateRange: 'Feb 1 to Feb 27, 2026',
    printLabel: 'Print',
    whyItMatters: {
      title: 'Why This Matters',
      body: 'The thirty day view confirms two sustained patterns that require formal intervention. The 412 Evangeline St situation progressed from a noise complaint to repeated domestic calls with documented tone escalation. The Bayou Oaks Park juvenile group has been active throughout the month with increasing frequency. Both situations have crossed the threshold for informal monitoring.',
    },
    sections: {
      weeklyOverview: {
        title: 'Monthly Overview',
        body: '98 calls for service over thirty days. Domestic and juvenile categories account for the largest increases. Seven arrests made. Property crime stable.',
        bullets: [
          'Domestic calls: 16 over thirty days.',
          'Juvenile contacts: 22 total. Majority at Bayou Oaks Park.',
          'LA-31 activity began mid-month. Now established pattern.',
          'Officer coverage consistent. No extended gaps.',
        ],
        statRows: [
          {
            label: 'Domestic calls',
            total: '16 total',
            wk1: 5,
            wk2: 11,
            delta: 6,
          },
          {
            label: 'Juvenile contacts',
            total: '22 total',
            wk1: 8,
            wk2: 14,
            delta: 6,
          },
          {
            label: 'LA-31 activity',
            total: '7 total',
            wk1: 1,
            wk2: 6,
            delta: 5,
          },
          { label: 'Arrests made', total: '7 total', wk1: 2, wk2: 5, delta: 3 },
        ],
      },
      notablePatterns: {
        title: 'Notable Patterns',
        subSections: [
          {
            title: 'Domestic Incidents',
            body: '412 Evangeline St had eight contacts over thirty days. Started as a noise complaint and escalated to repeated domestic calls. Marcus Johnson present at seven of eight contacts. Tone escalation documented by multiple officers.',
          },
          {
            title: 'Property Crimes',
            body: 'Five vehicle-related incidents across the month. Two break-ins and three suspicious vehicle stops. LA-31 corridor accounts for three of five incidents.',
          },
          {
            title: 'Juvenile Contacts',
            body: 'Bayou Oaks Park juvenile group contacted 22 times over thirty days. Devin Torres identified at 14 of those contacts. Late-night contacts increased significantly in the final two weeks.',
          },
          {
            title: 'Community Issues',
            body: 'Three welfare checks at 88 Cypress St. Four trespassing complaints on Sugar Mill Rd. Both locations became repeat contact sites this month.',
          },
        ],
      },
      priorityEntities: {
        title: 'Priority Entities',
        items: [
          {
            name: 'Marcus Johnson',
            context:
              'Present at 7 of 8 Evangeline contacts this month. Escalation documented by multiple officers.',
          },
          {
            name: '412 Evangeline St',
            context:
              'Eight contacts over thirty days. Escalation trajectory clear from first contact to most recent.',
          },
          {
            name: 'Devin Torres',
            context:
              'Fourteen contacts at Bayou Oaks Park this month. Consistent late-night presence. No formal intervention yet.',
          },
          {
            name: 'Bayou Oaks Park',
            context:
              'Twenty two juvenile contacts over thirty days. Activity increasing in frequency.',
          },
        ],
      },
      operationalGaps: {
        title: 'Operational Gaps',
        bullets: [
          '412 Evangeline St pattern active for thirty days. No safety plan or formal referral initiated.',
          'Bayou Oaks Park juvenile group active all month. No outreach or intervention engaged.',
          'LA-31 activity established. Overnight patrol not yet adjusted.',
          'Sugar Mill Rd trespassing unresolved for four weeks. No-trespass process not initiated.',
        ],
      },
      recommendedActions: {
        title: 'Recommended Actions',
        items: [
          'Initiate formal safety planning review for 412 Evangeline St.',
          'Engage school liaison for Bayou Oaks juvenile group.',
          'Establish dedicated LA-31 overnight patrol between 0200 and 0400.',
          'Issue no-trespass guidance and documentation for Sugar Mill Rd property owner.',
          'Schedule welfare check follow-up at 88 Cypress St.',
          'Conduct supervisory review of all 412 Evangeline St documentation this month.',
        ],
      },
      sourceNotes: {
        title: 'Source Notes',
        body: '142 notes reviewed across thirty days including call logs, field notes, and arrest reports. Last updated Feb 27, 2026 at 06:14. Source confidence high.',
      },
    },
  },
}

// ─── Top Bar ──────────────────────────────────────────────────────────────────

export const topBarCopy = {
  departmentNameFull: 'Imperian Police Department',
  departmentNameShort: 'Imperian PD',
  appName: 'Blue Partner Notes',
  dateRangeOptions: ['Past 7 days', 'Past 14 days', 'Past 30 days'],
}
